/**
 * SvlanChecker – reusable AJAX SVLAN availability checker.
 *
 * Usage:
 *   import SvlanChecker from 'shared/SvlanChecker';
 *
 *   this.svlanChecker = new SvlanChecker({
 *     checkUrl:       $('#svlan').data('svlan-check-url'),
 *     submitSelector: '#order-form-submit',
 *   });
 *
 *   // Clean up when the page controller is destroyed:
 *   this.svlanChecker.destroy();
 *
 * The check is skipped when:
 *   - the SVLAN field is hidden (no PDC order selected)
 *   - the SVLAN value is empty (server will auto-assign)
 *   - the value is out of the [2–4091] range (handled by existing range validation)
 */
import Swal from 'sweetalert2';

export default class SvlanChecker {
  /**
   * @param {object} opts
   * @param {string}  opts.checkUrl       – backend endpoint URL
   * @param {string}  opts.submitSelector – CSS selector for the submit button(s)
   * @param {string} [opts.pdcSelector]   – CSS selector for the PDC order select (default: '.js-pdc-select')
   * @param {string} [opts.backupPdcSelector] – CSS selector for the backup PDC select of a
   *                                            secured order (default: '.js-backup-pdc-select')
   * @param {string} [opts.securedToggleSelector] – CSS selector for the secured collect switch
   * @param {number} [opts.debounce]      – debounce delay in ms (default: 600)
   * @param {number|string} [opts.orderId] – id of the order being edited, so its own
   *                                         SVLAN isn't reported as taken by itself
   */
  constructor({
    checkUrl,
    submitSelector,
    pdcSelector = '.js-pdc-select',
    backupPdcSelector = '.js-backup-pdc-select',
    securedToggleSelector = '.js-secured-collect-toggle',
    debounce = 600,
    orderId = null
  }) {
    this.checkUrl       = checkUrl;
    this.submitSelector = submitSelector;
    this.pdcSelector    = pdcSelector;
    this.backupPdcSelector = backupPdcSelector;
    this.securedToggleSelector = securedToggleSelector;
    this.debounce       = debounce;
    this.orderId        = orderId;

    this._rangeExhausted = false; // true when the selected PDC has no free SVLAN

    this.$svlanInput = $('#svlan');
    this.$svlanGroup = $('#svlan-group');
    this.$svlanError = $('#svlan-error');
    this.$svlanRangeHint = $('#svlan-range-hint');

    // Allowed SVLAN range for the selected PDC. Defaults to the input's
    // server-rendered min/max (global default) until a PDC range is fetched.
    this._svlanMin = this._readBound('min', 2);
    this._svlanMax = this._readBound('max', 4091);

    this._timer          = null;
    this._available       = true;  // optimistic default
    this._checking        = false; // true while AJAX/debounce is in-flight
    this._lastCheckedSvlan = null; // svlan value of the last completed check
    this._lastCheckedPdc   = null; // PDC order id of the last completed check

    if (!this.checkUrl || this.$svlanInput.length === 0) return;

    this._bindEvents();
    this._fetchRange(); // sync bounds/hint with the currently selected PDC (if any)
  }

  // ── Public ────────────────────────────────────────────────────────────────

  /** Returns true when the field is either hidden, empty, or the last check passed.
   *  Stays true while a check is in-flight so navigation isn't blocked unnecessarily;
   *  the submit button is separately disabled during that window.
   */
  get isAvailable() {
    if (!this._isFieldVisible()) return true;
    // A saturated PDC range blocks submit even with the field empty: the server
    // would otherwise auto-assign nothing and save an SVLAN-less order.
    if (this._rangeExhausted) return false;
    if (!this.$svlanInput.val()) return true;
    // Only block if we have a *confirmed* negative – not while still checking
    return this._available;
  }

  /** True while the debounce timer or AJAX request is in-flight. */
  get isChecking() {
    return this._checking;
  }

  destroy() {
    clearTimeout(this._timer);
    this.$svlanInput.off('input.svlanChecker change.svlanChecker');
    $(document).off('change.svlanChecker', this.pdcSelector);
    $(document).off('change.svlanChecker', this.backupPdcSelector);
    $(document).off('change.svlanChecker', this.securedToggleSelector);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  _bindEvents() {
    this.$svlanInput.on('input.svlanChecker change.svlanChecker', () => this._onSvlanChange());
    $(document).on('change.svlanChecker', this.pdcSelector, () => this._onPdcChange());
    // A secured order reserves its SVLAN in the range mutualized between both
    // collect gates: changing the backup gate (or the option itself) changes the pool.
    $(document).on('change.svlanChecker', this.backupPdcSelector, () => this._onPdcChange());
    $(document).on('change.svlanChecker', this.securedToggleSelector, () => this._onPdcChange());
  }

  _onPdcChange() {
    clearTimeout(this._timer);
    this._checking        = false;
    this._lastCheckedSvlan = null;
    this._lastCheckedPdc   = null;
    this._rangeExhausted   = false;
    this._clearStatus();
    this._setAvailable(true);
    this._fetchRange();
  }

  _onSvlanChange() {
    clearTimeout(this._timer);

    const raw = this.$svlanInput.val();

    if (!raw) {
      this._checking = false;
      this._clearStatus();
      this._setAvailable(true);
      return;
    }

    const num = parseInt(raw, 10);
    if (isNaN(num) || num < this._svlanMin || num > this._svlanMax) {
      // Out-of-range – confirmed invalid; block submit but don't set _checking.
      this._checking = false;
      this._lastCheckedSvlan = null;
      this._setAvailable(false);
      return;
    }

    // The browser fires a `change` event on number inputs when they lose focus
    // (e.g. the user clicks the submit button). If the value and PDC haven't
    // changed since the last completed check we already have the result – skip
    // the AJAX call so the submit click isn't blocked by a redundant re-check.
    if (raw === this._lastCheckedSvlan && this._pdcCacheKey() === this._lastCheckedPdc) {
      return;
    }

    this._showChecking(); // sets _checking = true, disables submit
    this._timer = setTimeout(() => this._doCheck(), this.debounce);
  }

  _doCheck() {
    const svlan      = this.$svlanInput.val();
    const pdcOrderId = $(this.pdcSelector).val();

    if (!svlan || !pdcOrderId) {
      this._clearStatus();
      this._setAvailable(true);
      return;
    }

    const cacheKey = this._pdcCacheKey();

    $.ajax({
      url:      this.checkUrl,
      method:   'GET',
      dataType: 'json',
      data:     this._requestData({ svlan, pdc_order_id: pdcOrderId }),
      success: (data) => {
        this._checking        = false;
        this._lastCheckedSvlan = svlan;
        this._lastCheckedPdc   = cacheKey;
        if (data.available) {
          this._showAvailable();
          this._setAvailable(true);
        } else {
          this._showUnavailable(data.message);
          this._setAvailable(false);
        }
      },
      error: () => {
        // On network error – clear status, don't block the form.
        this._checking        = false;
        this._lastCheckedSvlan = null; // don't cache on error
        this._clearStatus();
        this._setAvailable(true);
      },
    });
  }

  // ── SVLAN range (per PDC) ──────────────────────────────────────────────────

  /** Backup collect gate of a secured order, or null. */
  _backupPdcOrderId() {
    const $toggle = $(this.securedToggleSelector);
    if ($toggle.length > 0 && !$toggle.is(':checked')) return null;

    return $(this.backupPdcSelector).val() || null;
  }

  /** Adds the backup gate to a request payload when the order is secured, and the
   *  edited order so its own allocation isn't counted against it. */
  _requestData(data) {
    const payload = { ...data };
    const backupPdcOrderId = this._backupPdcOrderId();
    if (backupPdcOrderId) payload.backup_pdc_order_id = backupPdcOrderId;
    if (this.orderId) payload.order_id = this.orderId;
    return payload;
  }

  /** Identifies the collect gates a completed check was run against. */
  _pdcCacheKey() {
    return `${$(this.pdcSelector).val() || ''}|${this._backupPdcOrderId() || ''}`;
  }

  _readBound(attr, fallback) {
    const value = parseInt(this.$svlanInput.attr(attr), 10);
    return isNaN(value) ? fallback : value;
  }

  /** Fetch the allowed SVLAN range for the currently selected PDC. */
  _fetchRange() {
    const pdcOrderId = $(this.pdcSelector).val();
    if (!pdcOrderId) {
      this._resetRange();
      return;
    }

    $.ajax({
      url:      this.checkUrl,
      method:   'GET',
      dataType: 'json',
      data:     this._requestData({ pdc_order_id: pdcOrderId }),
      success: (data) => {
        if (data && data.svlan_min != null && data.svlan_max != null) {
          this._applyRange(data.svlan_min, data.svlan_max);
        }
        if (data && data.range_exhausted) {
          this._handleRangeExhausted(data.message);
        }
      },
    });
  }

  /** PDC's SVLAN range is fully allocated: warn the user and block submit. */
  _handleRangeExhausted(message) {
    this._rangeExhausted = true;
    this._setAvailable(false);
    this.$svlanInput.prop('disabled', true);

    const text = message
      || 'Aucun SVLAN disponible pour cette porte de collecte. '
         + 'Veuillez choisir une autre porte de collecte ou contacter le support.';

    Swal.fire({
      title: '<i class="mdi mdi-alert-outline text-warning mr-1"></i> Aucun SVLAN disponible',
      text,
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: { confirmButton: 'btn btn-success mt-2' }
    }).then(() => this._deselectPdc());
  }

  /** Reset the PDC select after the user acknowledges the saturated range. */
  _deselectPdc() {
    $(this.pdcSelector).val('').trigger('change');
  }

  _applyRange(min, max) {
    this._svlanMin = min;
    this._svlanMax = max;
    // Re-enable in case a previously selected PDC had a saturated range.
    this.$svlanInput.prop('disabled', false).attr('min', min).attr('max', max);
    this.$svlanError.text(`Le SVLAN doit être compris entre ${min} et ${max}.`);

    if (this.$svlanRangeHint.length) {
      $('#svlan-range-min').text(min);
      $('#svlan-range-max').text(max);
      this.$svlanRangeHint.show();
    }

    // Re-validate the current value against the new bounds.
    if (this.$svlanInput.val()) this._onSvlanChange();
  }

  _resetRange() {
    this._svlanMin = 2;
    this._svlanMax = 4091;
    this._rangeExhausted = false;
    this.$svlanInput.prop('disabled', false).attr('min', 2).attr('max', 4091);
    if (this.$svlanRangeHint.length) this.$svlanRangeHint.hide();
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  _showChecking() {
    this._checking = true;
    this._available = true; // remain optimistic until confirmed otherwise
    this.$svlanInput.removeClass('is-valid is-invalid');
    this.$svlanError.hide();
    this._statusEl()
      .html('<span class="spinner-border spinner-border-sm mr-1" role="status"></span> Vérification…')
      .show();
    // Disable submit while checking, but don't touch _available
    $(this.submitSelector)
      .prop('disabled', this._isFieldVisible())
      .toggleClass('disabled', this._isFieldVisible())
      .attr('title', this._isFieldVisible() ? 'Vérification du SVLAN en cours…' : null);
  }

  _showAvailable() {
    this.$svlanInput.removeClass('is-invalid').addClass('is-valid');
    this.$svlanError.hide();
    this._statusEl()
      .html('<span class="text-success"><i class="mdi mdi-check-circle"></i> SVLAN disponible</span>')
      .show();
  }

  _showUnavailable(message) {
    this.$svlanInput.addClass('is-invalid').removeClass('is-valid');
    this.$svlanError
      .text(message || 'Ce SVLAN est déjà utilisé pour cette porte de collecte.')
      .show();
    this._statusEl().hide();
  }

  _clearStatus() {
    this.$svlanInput.removeClass('is-valid is-invalid');
    this.$svlanError.hide();
    this._statusEl().hide();
  }

  _statusEl() {
    let $el = $('#svlan-availability-status');
    if ($el.length === 0) {
      $el = $('<div id="svlan-availability-status" class="small mt-1"></div>').hide();
      this.$svlanInput.after($el);
    }
    return $el;
  }

  _isFieldVisible() {
    return this.$svlanGroup.length > 0 && this.$svlanGroup.is(':visible');
  }

  _setAvailable(available) {
    this._available = available;

    const block = this._isFieldVisible() && !available;
    $(this.submitSelector)
      .prop('disabled', block)
      .toggleClass('disabled', block)
      .attr('title', block ? 'Résolvez le conflit SVLAN avant de soumettre.' : null);
  }
}
